import { Injectable } from '@angular/core';
import {
  consumeAuthHandoffPayload,
  storeAuthHandoffPayload,
} from 'app/auth/helpers/auth-handoff.util';

/** Clés session logiciel caisse à propager entre sous-domaines ERP. */
const LC_BASE_KEYS = ['logiciel_caisse_token', 'logiciel_caisse_session'] as const;

@Injectable({ providedIn: 'root' })
export class AuthSyncService {
  private pendingGoto: string | null = null;

  syncFromQueryParams(): boolean {
    const params = new URLSearchParams(window.location.search);
    const auth = params.get('auth');
    const goto = params.get('goto');

    if (goto) {
      this.pendingGoto = goto;
    }

    const fromCookie = consumeAuthHandoffPayload();
    if (fromCookie) {
      this.applyAuthPayload(fromCookie);
      this.stripAuthFromUrl();
      return true;
    }

    if (!auth) {
      if (goto) {
        this.stripAuthFromUrl();
      }
      return false;
    }

    try {
      const data = JSON.parse(atob(decodeURIComponent(auth))) as Record<string, unknown>;
      this.applyAuthPayload(data);
    } catch {
      return false;
    }

    this.stripAuthFromUrl();
    return true;
  }

  consumeGoto(): string | null {
    const goto = this.pendingGoto;
    this.pendingGoto = null;
    return goto;
  }

  /**
   * Prépare le transfert de session.
   * Sur *.agrosilo-solutions.com : cookie partagé (URL sans JWT) → chaîne vide.
   * Sinon (localhost) : repli historique `auth=` base64.
   */
  buildAuthQueryParams(): string {
    const payload = this.buildAuthPayload();
    if (storeAuthHandoffPayload(payload)) {
      return '';
    }
    return encodeURIComponent(btoa(JSON.stringify(payload)));
  }

  private buildAuthPayload(): Record<string, unknown> {
    return {
      token: localStorage.getItem('token') || localStorage.getItem('erp_token'),
      users: localStorage.getItem('users'),
      currentUser: localStorage.getItem('currentUser'),
      ...this.collectLogicielCaisseForAuthPayload(),
    };
  }

  private applyAuthPayload(data: Record<string, unknown>): void {
    if (data.token) {
      localStorage.setItem('token', String(data.token));
      localStorage.setItem('erp_token', String(data.token));
    }
    if (data.users) {
      localStorage.setItem('users', String(data.users));
    }
    if (data.currentUser) {
      localStorage.setItem('currentUser', String(data.currentUser));
    }
    this.applyLogicielCaisseFromAuthPayload(data);
  }

  private stripAuthFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    params.delete('auth');
    params.delete('handoff');
    const qs = params.toString();
    const cleanUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }

  private collectLogicielCaisseForAuthPayload(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const key of LC_BASE_KEYS) {
      const v = localStorage.getItem(key);
      if (v) {
        out[key] = v;
      }
    }
    try {
      const raw = localStorage.getItem('currentUser');
      const uid = raw ? Number(JSON.parse(raw)?.id) : 0;
      if (Number.isFinite(uid) && uid > 0) {
        for (const prefix of ['logiciel_caisse_code_', 'logiciel_caisse_access_cache_']) {
          const key = `${prefix}${uid}`;
          const v = localStorage.getItem(key);
          if (v) {
            out[key] = v;
          }
        }
      }
    } catch {
      /* ignore */
    }
    return out;
  }

  private applyLogicielCaisseFromAuthPayload(data: Record<string, unknown>): void {
    for (const key of LC_BASE_KEYS) {
      const v = data[key];
      if (typeof v === 'string' && v.trim()) {
        localStorage.setItem(key, v);
      }
    }
    for (const [key, value] of Object.entries(data)) {
      if (
        typeof value === 'string' &&
        value.trim() &&
        (key.startsWith('logiciel_caisse_code_') || key.startsWith('logiciel_caisse_access_cache_'))
      ) {
        localStorage.setItem(key, value);
      }
    }
  }
}
